import { createRouter, createWebHistory } from 'vue-router';
import { useStore } from "vuex";
import routeConnections from '@/modules/connections/router';
import fetcherLogsRouter from '@/modules/fetcher-logs/router';
import { useVFAuth } from '@/modules/auth/composables';
import { is_token_valid } from '@/helpers';
import { onErrorNotification } from '@/helpers/viewNotifications';

const Login = () => import('@/modules/auth/views/LoginView.vue');
const Home = () => import('@/views/HomeView.vue');

const routes = [
    {
        path: '/',
        name: "Login",
        component: Login,
        meta: {
            in_menu: false
        }
    },
    {
        path: '/home',
        name: "Inicio",
        component: Home,
        meta: {
            in_menu: true,
            icon: 'fas fa-home',
            requiresAuth: false
        }
    },
    ...routeConnections,
    ...fetcherLogsRouter
]

const router = createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: "active",
});

function handleRedirectData(to, store, next) {
    try {
        const redirect_data = Object.fromEntries(
            new URLSearchParams(atob(to.query.redirect_data))
        );
        if (redirect_data.token) {
            store.commit('auth/setToken', redirect_data.token);
        }
        if (redirect_data.refresh_token) {
            store.commit('auth/setRefreshToken', redirect_data.refresh_token);
        }
        if (redirect_data.client) {
            store.commit('auth/setClient', redirect_data.client);
        }
        const urlBack = redirect_data.url_referrer ?? document.referrer ?? '';
        store.commit('auth/setUrlBack', urlBack);
        const newQuery = { ...to.query };
        delete newQuery.redirect_data;
        next({
            path: to.path,
            query: newQuery,
            replace: true
        });
        return true;
    } catch (error) {
        return false;
    }
}

async function handleAuth(to, store, next, onRefreshToken) {
    const tokenFromStorage = localStorage.getItem("EXC-token");
    const refreshToken = localStorage.getItem("EXC-refreshToken");
    if (!store.getters['auth/token'] && tokenFromStorage) {
        store.commit('auth/setToken', tokenFromStorage);
    }
    const hasToken = store.getters['auth/token'] || tokenFromStorage;
    if (!hasToken) {
        if (to.path !== '/home') {
            next({
                path: "/home",
                params: { nextUrl: to.fullPath },
            });
        } else {
            next();
        }
        return;
    }
    if (is_token_valid(tokenFromStorage)) {
        next();
    } else {
        if (refreshToken) {
            const result = await onRefreshToken(refreshToken);
            if (result && result.data && result.data.ok) {
                store.commit('auth/setToken', result.data.token);
                store.commit('auth/setRefreshToken', result.data.refresh_token);
                next();
            } else {
                onErrorNotification(
                    window.$toast,
                    'No se pudo refrescar la sesión. Por favor, vuelve a iniciar sesión.'
                );
                if (to.path !== '/home') {
                    next({ path: '/home' });
                } else {
                    next();
                }
            }
        } else {
            onErrorNotification(
                window.$toast,
                'No se pudo refrescar la sesión. Por favor, vuelve a iniciar sesión.'
            );
            if (to.path !== '/home') {
                next({ path: '/home' });
            } else {
                next();
            }
        }
    }
}

router.beforeEach(async (to, _, next) => {
    const store = useStore();
    const { onRefreshToken } = useVFAuth();
    if (to.query.redirect_data) {
        if (handleRedirectData(to, store, next)) return;
    }
    if (to.matched.some((record) => record.meta.requiresAuth)) {
        await handleAuth(to, store, next, onRefreshToken);
    } else {
        next();
    }
});

export default router;
